"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useMemo } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { trackAction } from "../trackActions";
import { trackPostHog } from "../scripts/posthog";
import { fireCheckoutExposure } from "../experiments/checkoutExposure";
import { BloomreachLoad } from "../scripts/bloomreach";
import { ExternalTrackingBeacon } from "../ExternalTrackingBeacon";
import { LuigisBox } from "../scripts/luigisbox";
import { NuClickBeacon } from "./NuClickBeacon";
import { Analytics } from "@vercel/analytics/next";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { I18n } from "@local/offer/src/lib/i18n";
import type { BeaconData } from "./types";
interface Props {
  data: BeaconData;
}
export const StoreTrackingScripts = t0 => {
  const $ = _c(25);
  const {
    data
  } = t0;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const urlParams = useParams();
  const market = urlParams.market ? urlParams.market as string : "us";
  let t1;
  if ($[0] !== market) {
    t1 = I18n.getMarketData(market);
    $[0] = market;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const marketData = t1;
  let t2;
  if ($[2] !== marketData.currencyCode || $[3] !== marketData.locale) {
    t2 = {
      currencyCode: marketData.currencyCode,
      locale: marketData.locale
    };
    $[2] = marketData.currencyCode;
    $[3] = marketData.locale;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    currencyCode,
    locale
  } = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(_temp2, t3);
  let t4;
  if ($[6] !== data) {
    t4 = () => {
      if (!data) {
        return;
      }
      fireCheckoutExposure(window.location.pathname);
      const url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      trackPostHog("$pageview", {
        $current_url: url,
        channel: window.channel ?? "store"
      });
      trackAction("page_visit", {
        url
      }, {
        exp: false,
        ga: false,
        datalake: true,
        vercel: false
      });
    };
    $[6] = data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== data || $[9] !== pathname) {
    t5 = [pathname, data];
    $[8] = data;
    $[9] = pathname;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(t4, t5);
  if (!data) {
    return null;
  }
  let t6;
  let t7;
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <NuClickBeacon />;
    t7 = <LuigisBox />;
    t8 = <ExternalTrackingBeacon />;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== searchParams) {
    t9 = searchParams.toString();
    $[14] = searchParams;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = `bloomreach-${pathname}-${t9}`;
  let t11;
  if ($[16] !== currencyCode || $[17] !== data || $[18] !== locale || $[19] !== t10) {
    t11 = <BloomreachLoad key={t10} data={data} overrideLocale={locale} overrideCurrencyCode={currencyCode} />;
    $[16] = currencyCode;
    $[17] = data;
    $[18] = locale;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Analytics beforeSend={_temp3} />;
    t13 = <SpeedInsights beforeSend={_temp4} sampleRate={0.1} />;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11) {
    t14 = <Suspense>{t6}{t7}{t8}{t11}{t12}{t13}</Suspense>;
    $[23] = t11;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  return t14;
};
function _temp() {
  const missing = [];
  if (!window.exponea?.track) {
    missing.push("exponea");
  }
  if (!window.gtag) {
    missing.push("gtag");
  }
  if (missing.length > 0) {
    console.warn(`[TRACKING_SDK_MISSING] SDKs not loaded after 10s: ${missing.join(", ")}`);
  }
}
function _temp2() {
  const timer = setTimeout(_temp, 10000);
  return () => clearTimeout(timer);
}
function _temp3(d) {
  if (d.url.includes("/checkout") || d.url.includes("/order") || d.url.includes("/up")) {
    return null;
  }
  return d;
}
function _temp4(d_0) {
  if (d_0.url.includes("/checkout") || d_0.url.includes("/order")) {
    return null;
  }
  return d_0;
}